import { EyeOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Form,
  Grid,
  Image,
  Input,
  message,
  Spin,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { Modal } from 'components';
import { useRequestManagementUpload } from 'features/assigned-clients/use-cases/create-report';
import { useUploadImage } from 'features/assigned-clients/use-cases/upload-image';
import { StyledUserModalWrapper } from 'features/users/components/modals/user-modal.styles';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { convertFileSize, validateFileBeforeUpload } from 'routes/utils';

const { useBreakpoint } = Grid;

export const AddReportView = ({
  onClose,
  refetch,
  year,
  month,
  initilaMarkFinished,
}: {
  onClose: () => void;
  refetch: any;
  year: any;
  month: any;
  initilaMarkFinished: any;
}) => {
  const { id } = useParams();
  const formName = 'add-user-form';
  const screens = useBreakpoint();
  const { isLoading: reportLoading, mutate: mutateReport } =
    useRequestManagementUpload();
  const { isLoading: imageLoading, mutate: uploadImage } = useUploadImage();

  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [blobUrl, setBlobUrl] = useState<string>(''); // Store the uploaded blob URL
  const [filenameUrl, setFileNameUrl] = useState('');
  const [setPreviewImage] = useState<any>('');
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [isImageUploading, setIsImageUploading] = useState<boolean>(false);
  const [form] = Form.useForm();

  const handleImageUpload = (file: UploadFile) => {
    const formData = new FormData();
    formData.append('file', file as any);

    setIsImageUploading(true);

    uploadImage(
      { id: id as string, formData },
      {
        onSuccess: (response: any) => {
          setBlobUrl(response.blobUrl);
          setFileNameUrl(response.fileName);
          message.success('File uploaded successfully');
          setIsImageUploading(false);
        },
        onError: () => {
          message.error('file upload failed');
          setIsImageUploading(false);
        },
      },
    );
  };

  const handleUpdate = (formValues: any) => {
    if (!blobUrl) {
      return message.error('Please upload an file before saving');
    }
    const formData = new FormData();
    formData.append('DocumentName', formValues.DocumentName);
    formData.append('CompanyId', id as any);
    formData.append('Note', formValues.Note);
    formData.append('Month', month);
    formData.append('Year', year);

    formData.append('file', blobUrl);

    const uploadedFile = fileList[0]?.originFileObj;

    if (uploadedFile) {
      const fileSize = uploadedFile.size;
      formData.append('FileName', filenameUrl);
      formData.append('FileSize', convertFileSize(fileSize));
    }

    mutateReport(formData, {
      onSuccess: () => {
        refetch();
        initilaMarkFinished();
        onClose();
      },
    });
  };

  const handlePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      const reader = new FileReader();
      reader.readAsDataURL(file.originFileObj as Blob);
      reader.onload = () => {
        setPreviewImage(reader.result as string);
        setPreviewOpen(true);
      };
    } else {
      setPreviewImage(file.url || (file.preview as string));
      setPreviewOpen(true);
    }
  };

  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) => {
    setFileList(newFileList);
    if (newFileList.length === 0) {
      setBlobUrl(''); // Reset blob URL if file is removed
    }
  };

  const uploadButton = isImageUploading ? (
    <div>
      <Spin indicator={<LoadingOutlined />} />
      <div style={{ marginTop: 8 }}>Uploading</div>
    </div>
  ) : (
    <div>
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>Upload</div>
    </div>
  );

  const beforeUpload = (file: any) => {
    const isValid = validateFileBeforeUpload(file);
    if (isValid) {
      handleImageUpload(file);
    }

    return isValid;
  };

  return (
    <StyledUserModalWrapper>
      <Modal
        open
        centered
        title={<span className='font-semibold'>Upload Documents</span>}
        onCancel={onClose}
        getContainer={false}
        onOk={form.submit}
        okText={'Upload'}
        cancelButtonProps={{ style: { display: 'none' } }}
        width={screens.lg ? '520px' : '100%'}
        okButtonProps={{
          className: 'btn cstmfull',
          form: formName,
          loading: reportLoading,
          disabled: imageLoading || isImageUploading,
        }}
        maskClosable={false}
      >
        <Form
          id={formName}
          name={formName}
          form={form}
          layout='vertical'
          onFinish={handleUpdate}
          requiredMark={true}
        >
          <Form.Item
            label='Report Name'
            name='DocumentName'
            rules={[{ required: true, message: 'Please enter report name' }]}
          >
            <Input placeholder='Enter Report Name' />
          </Form.Item>

          <Form.Item name={'file'} label='Upload Document'>
            <div className='updcbx'>
              <Upload
                action='https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload'
                listType='picture-card'
                fileList={fileList}
                onPreview={handlePreview}
                onChange={handleChange}
                accept='.jpg,.jpeg,.png,.webp,.csv,.xlsx,.xls,.pdf'
                beforeUpload={beforeUpload}
                maxCount={1}
                className='upld_docs'
              >
                {fileList.length >= 1 ? null : uploadButton}
              </Upload>

              {blobUrl && fileList.length > 0 && (
                <EyeOutlined
                  className='eyeiconi'
                  onClick={() => setPreviewOpen(true)}
                />
              )}
            </div>

            {isImageUploading && (
              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 1,
                }}
              >
                <Spin size='large' />
              </div>
            )}

            <Modal
              open={previewOpen}
              onCancel={() => setPreviewOpen(false)}
              footer={null}
              centered
              width={800}
            >
              {blobUrl.endsWith('.jpg') ||
              blobUrl.endsWith('.jpeg') ||
              blobUrl.endsWith('.png') ||
              blobUrl.endsWith('.webp') ? (
                <Image src={blobUrl} alt='Preview' />
              ) : (
                <iframe
                  src={`https://docs.google.com/gview?url=${blobUrl}&embedded=true`}
                  style={{ width: '100%', height: '600px', border: 'none' }}
                  title='Document Preview'
                />
              )}
            </Modal>
          </Form.Item>

          <Form.Item
            label='Notes'
            name={'Note'}
            rules={[{ required: true, message: 'Please enter notes' }]}
          >
            <TextArea placeholder='Enter notes' />
          </Form.Item>
        </Form>
      </Modal>
    </StyledUserModalWrapper>
  );
};
